import * as React from 'react';
import {
  Alert,
  Box,
  Button,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { ThreadEntry } from '../types';
import { formatTime } from './common';

interface Props {
  thread: ThreadEntry[];
  placeholder: string;
  // Asks and resolves with the record's updated thread.
  onAsk: (question: string) => Promise<ThreadEntry[]>;
  onAddRule: (text: string) => Promise<void>;
}

// Questions about a decision or a run, answered from the logged records and
// saved on them, so the conversation stays with what it's about.
export default function AskBox({
  thread,
  placeholder,
  onAsk,
  onAddRule,
}: Props) {
  const [question, setQuestion] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [entries, setEntries] = React.useState(thread);
  const [added, setAdded] = React.useState<Set<string>>(new Set());

  React.useEffect(() => setEntries(thread), [thread]);

  const ask = async () => {
    setBusy(true);
    setError(null);
    try {
      setEntries(await onAsk(question.trim()));
      setQuestion('');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const addRule = async (text: string) => {
    setError(null);
    try {
      await onAddRule(text);
      setAdded(prev => new Set(prev).add(text));
    } catch (err: any) {
      setError(err.message);
    }
  };

  return (
    <Stack spacing={1.5}>
      {entries.map(e => (
        <Box key={e.at}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {e.question}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {e.askedBy || 'Someone'} · {formatTime(e.at)}
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
            {e.answer}
          </Typography>
          {e.suggestedRule && (
            <Alert
              severity="info"
              sx={{ mt: 1 }}
              action={
                added.has(e.suggestedRule) ? (
                  <Button size="small" disabled>
                    Added
                  </Button>
                ) : (
                  <Button size="small" onClick={() => addRule(e.suggestedRule)}>
                    Add as rule
                  </Button>
                )
              }
            >
              Suggested rule: “{e.suggestedRule}”
            </Alert>
          )}
        </Box>
      ))}
      <TextField
        id="seo-ask"
        multiline
        minRows={2}
        fullWidth
        placeholder={placeholder}
        value={question}
        onChange={e => setQuestion(e.target.value)}
        disabled={busy}
      />
      <Stack direction="row" spacing={1} alignItems="center">
        <LoadingButton
          variant="outlined"
          loading={busy}
          disabled={question.trim().length < 3}
          onClick={ask}
        >
          Ask
        </LoadingButton>
        <Typography variant="caption" color="text.secondary">
          {busy
            ? 'Reading the records… this can take up to a minute.'
            : 'Answered only from the logged data; saved here for everyone.'}
        </Typography>
      </Stack>
      {error && <Alert severity="error">{error}</Alert>}
    </Stack>
  );
}
